import { Injectable } from '@angular/core';
import * as c3 from 'c3';
import * as d3 from 'd3';
import { SettingsProvider } from '@services/settings/settings';
import {
	CustomizedLabels,
	GraphConfig,
	LegendConfig,
	LegendPattern
} from '@models/models';
import { revenueTabColors } from '@web-services/graph/graph.model';
import { isValid } from 'ngx-bootstrap/chronos/create/valid';

@Injectable()
export class GraphsService {
	chartObject: any;

	private shootsTabName: string;

	constructor(private settingsService: SettingsProvider) {
		this.shootsTabName = 'Shoots';
		this.subscribeToCustomizeLabelsChange();
	}

	static toggleLegendStyles(id): 'ADD' | 'REMOVE' {
		const legendClasses = document.querySelector(`[data-id="${id}"]`).classList;
		if (legendClasses.contains('disabled')) {
			legendClasses.remove('disabled');
			return 'ADD';
		}
		legendClasses.add('disabled');
		return 'REMOVE';
	}

	private subscribeToCustomizeLabelsChange(): void {
		this.settingsService.customizeLabelsObservable.subscribe(
			(customizeLabels: CustomizedLabels): string =>
				(this.shootsTabName = customizeLabels.shootsLabel)
		);
	}

	clear(): void {
		if (this.chartObject) {
			this.chartObject = this.chartObject.destroy();
		}
	}

	createLeadData(leadData, acceptedLeadsData, companyId: number): void {
		const chart = {
			json: this.mergeTwoArraysOfObjects(leadData, acceptedLeadsData),
			keys: {
				x: 'label',
				value: ['value', 'value1']
			},
			names: {
				value: 'Leads',
				value1: 'Jobs Accepted'
			},
			types: {
				value: 'area',
				value1: 'area'
			},
			colors: {
				value: '#f27987',
				value1: '#22baa1'
			},
			valueLabels: []
		};
		Object.keys(chart.json).forEach((objCount: string): void => {
			chart.valueLabels.push([
				{ value: chart.json[objCount].valueLabel },
				{ value1: chart.json[objCount].valueLabel1 }
			]);
		});
		const legendConfig: LegendConfig = {
			needLegend: true,
			legendRowLength: 2,
			id: `legendLeads-${companyId}`,
			pattern: 'NAME'
		};
		const generalConfig: GraphConfig = {
			shortPeriod: true,
			chartName: 'lead_chart'
		};
		this.drawGraph(chart, legendConfig, generalConfig);
	}

	createShootData(data, companyId: number): void {
		const chart = {
			json: data,
			keys: {
				x: 'label',
				value: ['value']
			},
			names: {
				value: this.shootsTabName
			},
			types: {
				value: 'area'
			},
			colors: {
				value: '#22baa1'
			},
			valueLabels: []
		};
		Object.keys(chart.json).forEach((objCount: string, index: number): void => {
			chart.valueLabels.push([{ value: chart.json[objCount].valueLabel }]);
		});
		const legendConfig: LegendConfig = {
			needLegend: false,
			legendRowLength: 2,
			id: `legendShoots-${companyId}`,
			pattern: 'NAME'
		};
		const generalConfig: GraphConfig = {
			shortPeriod: true,
			chartName: 'shoot_chart'
		};

		this.drawGraph(chart, legendConfig, generalConfig);
	}

	createPaymentsData(
		payments,
		paymentsDue,
		currency: string,
		companyId: number
	): void {
		const chart = {
			json: this.mergeTwoArraysOfObjects(payments, paymentsDue),
			keys: {
				x: 'label',
				value: ['value', 'value1']
			},
			names: {
				value: 'Payments Received',
				value1: 'Unpaid Payments'
			},
			types: {
				value: 'area',
				value1: 'area'
			},
			colors: {
				value: '#7a6fbe',
				value1: '#c2c2c2'
			},
			valueLabels: []
		};
		Object.keys(chart.json).forEach((objCount: string): void => {
			chart.valueLabels.push([
				{ value: chart.json[objCount].valueLabel },
				{ value1: chart.json[objCount].valueLabel1 }
			]);
		});
		const legendConfig: LegendConfig = {
			needLegend: true,
			legendRowLength: 2,
			id: `legendPayments-${companyId}`,
			pattern: 'NAME'
		};
		const generalConfig: GraphConfig = {
			shortPeriod: true,
			chartName: 'payment_chart',
			currency
		};
		this.drawGraph(chart, legendConfig, generalConfig);
	}

	createRevenueData(revenue, currency, companyId: number): void {
		const chart = {
			json: [],
			keys: {
				x: 'label',
				value: []
			},
			names: {},
			types: {},
			colors: {},
			valueLabels: []
		};

		Object.keys(revenue).forEach((year: string, index: number): void => {
			let key = 'value';
			if (index === 0) {
				chart.json = revenue[year].data;
			} else {
				chart.json = this.mergeTwoArraysOfObjects(
					chart.json,
					revenue[year].data,
					index
				);
				key = `value${index}`;
			}
			chart.keys.value.push(key);
			chart.names[key] = `${year} (${revenue[year].totalLabel})`;
			chart.types[key] = 'area';
			chart.colors[key] = revenueTabColors[index];
		});

		const keys = Object.keys(chart.json);
		keys.forEach((objCount: string): void => {
			const chartValues = [];
			chartValues.push({ value: chart.json[objCount].valueLabel });

			for (let i = 0; i < keys.length; i++) {
				if (i) {
					chartValues.push({
						[`value${i}`]: chart.json[objCount][`valueLabel${i}`]
					});
				}
			}

			chart.valueLabels.push(chartValues);
		});
		const legendConfig: LegendConfig = {
			needLegend: true,
			legendRowLength: 5,
			id: `legendRevenue-${companyId}`,
			pattern: 'YEAR'
		};
		const generalConfig: GraphConfig = {
			shortPeriod: false,
			chartName: 'revenue_chart',
			currency
		};
		this.drawGraph(chart, legendConfig, generalConfig);
	}

	drawGraph(data, legendConfig: LegendConfig, chartConfig: GraphConfig): void {
		if (this.chartObject) {
			this.chartObject = this.chartObject.destroy();
		}
		let legend;
		const container = d3.select('.chart-container');
		const chartObj = d3.select('#chart');
		const isRevenue = chartConfig.chartName === 'revenue_chart';
		const isLead = chartConfig.chartName === 'lead_chart';
		const isShoot = chartConfig.chartName === 'shoot_chart';
		const tickValues: Array<number> = this.calculateGridTicksValues(data.json);
		const tickCulling: number = this.calculateTickCulling(
			data.json.length,
			window.innerWidth
		);
		const shootName = this.shootsTabName;

		d3.select('.legend').remove();
		d3.select('.pie-chart-active-legend')
			.select('.legend-pie')
			.remove();
		chartObj.style('height', null);

		const chart = c3.generate({
			bindto: '#chart',
			data,
			transition: {
				duration: 0
			},
			grid: {
				y: {
					show: true
				}
			},
			axis: {
				x: {
					type: 'category',
					tick: {
						width: 100,
						culling: {
							max: tickCulling
						}
					}
				},
				y: this.setYaxis(isLead, isShoot, tickValues, chartConfig.currency)
			},
			tooltip: {
				format: {
					title(): string {
						return '';
					}
				},
				contents(values): string {
					const valuesCopy = JSON.parse(JSON.stringify(values));
					let template = '';
					for (let i = 0; i < valuesCopy.length; i++) {
						// const formattedValue = d3.format(',')(valuesCopy[i].value);
						let displayedValue = '';
						data.valueLabels[valuesCopy[i].index].forEach((obj, index) => {
							Object.keys(data.valueLabels[valuesCopy[i].index][index]).forEach(
								(key: string): void => {
									if (key === valuesCopy[i].id) {
										displayedValue =
											data.valueLabels[valuesCopy[i].index][index][key];
									}
								}
							);
						});
						switch (valuesCopy[i].name) {
							case 'Payments Received':
								valuesCopy[i].name = 'Received';
								break;
							case 'Unpaid Payments':
								valuesCopy[i].name = 'Unpaid';
								break;
							default:
								break;
						}
						template += `<p><span class='tooltip-name'>${
							valuesCopy[i].name === 'Received' ||
							valuesCopy[i].name === 'Unpaid' ||
							valuesCopy[i].name === shootName ||
							isLead
								? valuesCopy[i].name
								: valuesCopy[i].name.slice(0, 4)
						}:</span> <span class='tooltip-value'>${
							// currency ? currency + formattedValue : formattedValue
							displayedValue
						}</span></p>`;
					}
					return `<div class='tooltip-container'>
                              ${template}
                            </div>`;
				}
			},
			legend: {
				show: !!(
					!legendConfig.needLegend && data.names.value !== this.shootsTabName
				)
			},
			onresized(): void {
				const windowWidth: number = window.innerWidth;
				const tickCount: number = data.json.length;
				let culling: number;
				// need to think how to call calculateTickCulling to avoid duplicate code
				if (isRevenue) {
					GraphsService.trackRevenueWidthChange(
						legend,
						windowWidth,
						data.keys.value
					);
					GraphsService.resizeRevenueDueToLegend(container, chartObj, legend);
				}
				if (windowWidth > 1200 && windowWidth <= 1390 && tickCount > 26) {
					culling = Math.ceil(tickCount / 2);
				} else if (windowWidth > 600 && windowWidth <= 1200 && tickCount > 16) {
					culling = Math.ceil(tickCount / 2);
				} else if (windowWidth <= 600 && tickCount <= 16) {
					culling = Math.ceil(tickCount / 2);
				} else if (windowWidth <= 600 && tickCount > 16) {
					culling = Math.ceil(tickCount / 4);
				} else {
					culling = 0;
				}
				// quick solution to call "internal" property that doesn't exist in ChartAPI type, need to refactor this in future
				const internal = 'internal';
				chart[internal].config.axis_x_tick_culling_max = culling;
			}
		});
		this.chartObject = chart;
		if (legendConfig.needLegend) {
			GraphsService.setDefaultLegendValuesToStorage(legendConfig.id);

			const svg = d3.select('#chart').select('svg');
			svg.style('visibility', 'hidden');

			requestAnimationFrame((): void => {
				this.addLegend(chart, data.keys.value, legendConfig);
				if (isRevenue) {
					GraphsService.trackRevenueWidthChange(
						legend,
						window.innerWidth,
						data.keys.value
					);

					GraphsService.resizeRevenueDueToLegend(container, chartObj, legend);
				}
			});
			setTimeout(() => svg.style('visibility', 'visible'), 250);
		}
		requestAnimationFrame((): void => chart.flush());
	}

	private calculateTickCulling = (tickCount, windowWidth): number => {
		if (windowWidth > 1200 && windowWidth <= 1390 && tickCount > 26) {
			return Math.ceil(tickCount / 2);
		} else if (windowWidth > 600 && windowWidth <= 1200 && tickCount > 20) {
			return Math.ceil(tickCount / 2);
		} else if (windowWidth <= 600 && tickCount <= 16) {
			return Math.ceil(tickCount / 2);
		} else if (windowWidth <= 600 && tickCount > 16) {
			return Math.ceil(tickCount / 4);
		}
		return 0;
	};

	private calculateGridTicksValues = (data): Array<number> => {
		const ticks: Array<number> = [];
		let maxValue;
		let maxValue1 = 0;
		let maxValue2 = 0;
		let valueOfTick = 0;

		// find max value in three arrays
		maxValue = this.findMaxValueInArray(data, 'value');
		if (Object.prototype.hasOwnProperty.call(data[0], 'value1')) {
			maxValue1 = this.findMaxValueInArray(data, 'value1');
		}
		if (Object.prototype.hasOwnProperty.call(data[0], 'value2')) {
			maxValue2 = this.findMaxValueInArray(data, 'value2');
		}
		maxValue = Math.ceil(Math.max(maxValue, maxValue1, maxValue2));

		// check not to run an infinite loop
		if (!maxValue || maxValue === 0) {
			return [0, 1, 2, 3, 4];
		}

		// check whether the number is multiple four
		while (maxValue % 4) {
			maxValue++;
		}

		// divide axis Y into 4 equal parts
		while (valueOfTick <= maxValue) {
			ticks.push(valueOfTick);
			valueOfTick += maxValue / 4;
		}
		return ticks;
	};

	private findMaxValueInArray = function(array, valueName): number {
		return Math.max(...array.map((value): number => value[valueName]));
	};

	private setYaxis(isLead, isShoot, tickValues, currency): any {
		if (isLead || isShoot) {
			const setYaxisLead = {
				max: tickValues[tickValues.length - 1],
				min: 0,
				padding: { top: 5, bottom: 5 },
				tick: {
					format(tickValue: number): string {
						if (tickValue === 0) {
							return '';
						}
						const formattedTickValue = d3.format(',')(tickValue);
						return currency
							? `${currency} ${formattedTickValue}`
							: formattedTickValue;
					},
					values: tickValues
				}
			};
			return setYaxisLead;
		}
		const setYaxisRest = {
			min: 0,
			padding: { top: 5, bottom: 5 },
			tick: {
				format(tickValue: number): string {
					if (tickValue === 0) {
						return '';
					}
					const formattedTickValue = d3.format(',')(tickValue);
					return currency
						? `${currency} ${formattedTickValue}`
						: formattedTickValue;
				}
			}
		};
		return setYaxisRest;
	}

	private mergeTwoArraysOfObjects = function(
		arrayOne,
		arrayTwo,
		index = 1
	): Array<{}> {
		const newArray = [];
		arrayOne.forEach((objectOne): void => {
			arrayTwo.forEach((ObjectTwo): void => {
				if (objectOne.label === ObjectTwo.label) {
					if (!objectOne.hasOwnProperty(`value${index}`)) {
						newArray.push({
							...objectOne,
							[`value${index}`]: ObjectTwo.value,
							[`valueLabel${index}`]: ObjectTwo.valueLabel
						});
					}
				}
			});
		});
		return newArray;
	};

	private addLegend = function(
		chart,
		value: Array<string>,
		legendConfig: LegendConfig
	): void {
		const svg = d3.select('#chart').select('svg');
		d3.select('.chart-container')
			.append('div')
			.attr(
				'class',
				`legend ${GraphsService.generateLegendClasses(
					value.length,
					legendConfig.legendRowLength
				)}`
			)
			.selectAll('span')
			.data(value)
			.enter()
			.append('span')
			.attr('data-id', (id): string => id)
			.html((id): string => `<p>${chart.data.names()[id]}</p>`)
			.each(function(id): void {
				d3.select(this)
					.insert('div', 'p')
					.attr('class', 'legend-name')
					.style('background-color', chart.color(id));

				const isVisible = GraphsService.shouldLegendBeEnabled(
					legendConfig.id,
					GraphsService.getCorrectLegendValue(
						legendConfig.pattern,
						chart.data.names()[id]
					)
				);
				if (isVisible) {
					GraphsService.toggleLegendStyles(id);
					chart.toggle(id);
				}
			})
			.on('mouseover', (id): void => {
				const legendClasses = document.querySelector(`[data-id="${id}"]`)
					.classList;
				if (!legendClasses.contains('disabled')) {
					chart.focus(id);
				}
			})
			.on('mouseout', (): void => {
				chart.revert();
			})
			.on('click', (id): void => {
				svg.style('visibility', 'hidden');

				const action = GraphsService.toggleLegendStyles(id);
				chart.toggle(id);

				GraphsService.manageStoredLegend(
					legendConfig.id,
					action,
					GraphsService.getCorrectLegendValue(
						legendConfig.pattern,
						chart.data.names()[id]
					)
				);

				setTimeout(() => svg.style('visibility', 'visible'), 250);
			});
	};

	static trackRevenueWidthChange(
		legend,
		width: number,
		value: Array<string>
	): void {
		const maxLegendCount = width > 1199 ? 5 : 3;

		if (!legend) {
			legend = d3.select('.chart-container').select('.legend');
		}

		if (
			!legend
				.attr('class')
				.includes(
					`grid-columns-${GraphsService.calculateCorrectGridCount(
						value.length,
						maxLegendCount
					)}`
				)
		) {
			legend.attr(
				'class',
				`legend grid ${GraphsService.generateLegendClasses(
					value.length,
					maxLegendCount
				)}`
			);
		}
	}

	static calculateCorrectGridCount(
		elementsCount: number,
		maxAllowed: number
	): number {
		return elementsCount > maxAllowed ? maxAllowed : elementsCount;
	}

	static removeLegend(): void {
		d3.select('.legend').remove();
		d3.select('.legend-pie').remove();
	}

	static generateLegendClasses(
		elementsCount: number,
		maxAllowedRow: number
	): string {
		let legend = 'grid';

		elementsCount > maxAllowedRow
			? (legend += ` grid-columns-${maxAllowedRow} grid-items-start`)
			: (legend += ` grid-columns-${elementsCount} grid-items-center`);

		return legend;
	}

	static resizeRevenueDueToLegend(container, chart, legend): void {
		if (!legend) {
			legend = d3.select('.chart-container').select('.legend');
		}

		chart.style(
			'height',
			`${+container.style('height').slice(0, -2) -
				legend.style('height').slice(0, -2) -
				10}px`
		);
	}

	static setDefaultLegendValuesToStorage(storageId: string): void {
		if (!localStorage.getItem(storageId)) {
			localStorage.setItem(storageId, [].join(';'));
		}
	}

	static shouldLegendBeEnabled(storageId: string, value: string): boolean {
		return localStorage.getItem(storageId).indexOf(value) !== -1;
	}

	static manageStoredLegend(
		storageId: string,
		action: 'ADD' | 'REMOVE',
		value: string
	): void {
		let data = localStorage.getItem(storageId).split(';');

		if (action === 'ADD') {
			data = data.filter((val: string): boolean => val !== value);
		}

		if (action === 'REMOVE') {
			data.push(value);
		}

		localStorage.setItem(storageId, data.join(';'));
	}

	static getCorrectLegendValue(type: LegendPattern, value: string): string {
		switch (type) {
			case 'YEAR':
				return value.substring(0, 4);
			case 'NAME':
				return value;
			default:
				return value;
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/services/graph/graphs.service.ts